import { useRef, useState } from 'react'

import { Portal } from '../Portal'

import {
  BackgroundWrappedChildren,
  Container,
  ContainerWrappedChildren,
  ContainerWrappedComponent,
} from './ToolTip.styled'

interface Props {
  disabled?: boolean
  forceDisplay?: boolean
  position?: 'left' | 'right'
  WrappedComponent: (props: { display: boolean; hide: () => void }) => JSX.Element
  WrappedChildren: (props: { display: boolean; hide: () => void }) => JSX.Element
  dropDownWidth?: number
  hideOverToolTip?: boolean
}

function offset(element: HTMLElement) {
  const rect = element.getBoundingClientRect()
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop

  return { top: rect.top + scrollTop, left: rect.left + scrollLeft }
}

export const ToolTip = (props: Props): JSX.Element => {
  const {
    disabled,
    forceDisplay,
    WrappedComponent,
    WrappedChildren,
    position = 'left',
    dropDownWidth = 0,
    hideOverToolTip,
  } = props

  const refWrappedComponent = useRef<HTMLDivElement | null>(null)

  const [displayToolTip, setDisplayToolTip] = useState<boolean>(false)

  const containerStyle: React.CSSProperties = { position: 'absolute', zIndex: 10000 }
  const backgroundStyle: React.CSSProperties = {}

  if (refWrappedComponent.current) {
    const rect = offset(refWrappedComponent.current)

    if (position == 'left' && window.innerWidth - dropDownWidth > rect.left) {
      containerStyle.left = rect.left
    } else {
      containerStyle.right = document.documentElement.clientWidth - rect.left - refWrappedComponent.current.clientWidth
    }

    if (rect.top > window.innerHeight / 2) {
      backgroundStyle.paddingBottom = 5
      containerStyle.bottom = window.innerHeight - rect.top
    } else {
      backgroundStyle.paddingTop = 5
      containerStyle.top = rect.top + refWrappedComponent.current.clientHeight
    }
  }

  return (
    <Container>
      <ContainerWrappedComponent
        ref={(ref) => (refWrappedComponent.current = ref)}
        onMouseEnter={() => !forceDisplay && !disabled && setDisplayToolTip(true)}
        onMouseOver={() => !forceDisplay && !disabled && setDisplayToolTip(true)}
        onMouseLeave={() => !forceDisplay && !disabled && setDisplayToolTip(false)}
        onDrag={() => !forceDisplay && !disabled && setDisplayToolTip(false)}
      >
        {WrappedComponent({ display: displayToolTip, hide: () => setDisplayToolTip(false) })}
      </ContainerWrappedComponent>

      {(forceDisplay || displayToolTip) && (
        <Portal style={containerStyle}>
          <BackgroundWrappedChildren
            style={backgroundStyle}
            onMouseEnter={() => !forceDisplay && !disabled && !hideOverToolTip && setDisplayToolTip(true)}
            onMouseOver={() => !forceDisplay && !disabled && !hideOverToolTip && setDisplayToolTip(true)}
            onMouseLeave={() => !forceDisplay && !disabled && !hideOverToolTip && setDisplayToolTip(false)}
            onDrag={() => !forceDisplay && !disabled && !hideOverToolTip && setDisplayToolTip(false)}
          >
            <ContainerWrappedChildren>
              {WrappedChildren({ display: displayToolTip, hide: () => setDisplayToolTip(false) })}
            </ContainerWrappedChildren>
          </BackgroundWrappedChildren>
        </Portal>
      )}
    </Container>
  )
}
